<script setup lang="ts">
import { computed, type PropType } from 'vue';
import type { GaiaJsonValue } from '@/api';
import type { FieldType } from '@/types/evaluation';
import { formatDisplayValue, isEffectivelyEmpty } from '@/utils/displayAiResponse';
import GaiaText from '../../base/GaiaText.vue';

const props = defineProps({
  /** The raw extracted value, straight off the wire — untyped JSON. */
  value: {
    // Required rather than defaulted: `GaiaJsonValue` is `unknown`, and any
    // literal default collapses the inferred prop type to that literal — and a
    // cast that would preserve it is stripped as redundant by eslint --fix.
    // Pass an explicit `null` for "no value"; there is no row without one.
    type: [String, Number, Boolean, Array, Object] as PropType<GaiaJsonValue>,
    required: true,
  },
  /**
   * The prompt's declared response type, which decides how the value reads:
   * a bare number is a page count under `int` but a timestamp under `datetime`,
   * and an object is a currency, an entity or a calendar event depending on it.
   */
  fieldType: {
    type: [String, Object] as PropType<FieldType | undefined>,
    default: undefined,
  },
  /** Multi-line values (entities, schedules, lists) wrap instead of truncating. */
  multiline: {
    type: Boolean,
    default: true,
  },
  /**
   * One line, the rest cut off with an ellipsis — for a table row that keeps
   * one line per field. Wins over `multiline`.
   */
  truncate: {
    type: Boolean,
    default: false,
  },
});

const formatted = computed(() => formatDisplayValue(props.value, props.fieldType));

/**
 * Blank reads as blank. The formatter has two ways of saying "nothing here"
 * ("N/A" for a missing value, "Empty" for a present-but-empty one) and the row
 * should look the same either way, so the emptiness test is on the value.
 */
const empty = computed(() => isEffectivelyEmpty(props.value));

/**
 * Objects format to several lines (an entity's name and address, a schedule's
 * rule). They get a block that keeps those line breaks; scalars stay inline so
 * they sit on the label's baseline.
 */
const isBlock = computed(
  () => !props.truncate && props.multiline && typeof props.value === 'object' && !empty.value
);
</script>

<template>
  <!-- Read-only render of one extracted value: the grid cell's contents, the
       Response row of an extraction card, a row of the cell-details modal.
       Interpolated, never v-html — the value is model output. -->
  <gaia-text
    :tag="isBlock ? 'p' : 'span'"
    size="row"
    :tone="empty ? 'subtle' : 'ink'"
    :truncate="props.truncate"
    :class="{ block: isBlock, empty }"
  >
    {{ formatted }}
  </gaia-text>
</template>

<style scoped>
/* Selector-bound: only the block form keeps the formatter's newlines, and the
   empty form is the one that leans. Neither is expressible as a GaiaText prop. */
.block {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.empty {
  font-style: italic;
}
</style>
